import {QueryResultWrapper, useQuery} from '@apollo/client'
import {CSSObject} from '@emotion/react'
import {
  Box,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  Grid2,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import {differenceInYears, isThisMonth, isThisWeek} from 'date-fns'
import gql from 'graphql-tag'
import {chunk, groupBy, unionBy} from 'lodash-es'
import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useLocale, useTranslate} from 'react-admin'
import {GroupedVirtuoso} from 'react-virtuoso'
import {makeStyles} from 'tss-react/mui'

import ApiUrlsContext from '../../contexts/ApiUrlsContext'
import useDownloadFileAttachments from '../../hooks/useDownloadFileAttachments'
import useLongPressEvent from '../../hooks/useLongPressEvent'
import {ChatMessageFileAttachments, QueryRoot} from '../../types/graphqlSchema'
import {monthNameFromData} from '../../utils/dates'
import {CloseIcon, DownloadIcon, MoreVertIcon} from '../icons'
import FileAttachmentsSwiperDialog from './FileAttachmentsSwiperDialog'


const ChatMessageFileAttachmentListSidebar: FC<
  ChatMessageFileAttachmentListSidebarProps
> = ({chatId, onClose}) => {
  const {classes: styles} = useStyles({})
  const locale = useLocale()
  const translate = useTranslate()
  const [activeTab, setActiveTab] = useState(TAB_FILTERS[0])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const offsetRef = useRef(LOAD_MORE_OFFSET)
  const hasEarlierChatMessageFileAttachmentsRef = useRef(true)
  const [
    selectedFileAttachment, setSelectedFileAttachment,
  ] = useState<ChatMessageFileAttachments>()
  const [isSelectingFileAttachments, setIsSelectingFileAttachments] = useState(false)
  const [
    selectedFileAttachments,
    setSelectedFileAttachments,
  ] = useState<SelectedFileAttachments>(new Map())
  const onToggleIsSelectingFileAttachments = useCallback(() => {
    setIsSelectingFileAttachments(s => {
      s && setSelectedFileAttachments(new Map())
      return !s
    })
  }, [])
  const onToggleSelectedFileAttachments = useCallback(
    (f: ChatMessageFileAttachments) => {
      !isSelectingFileAttachments && setIsSelectingFileAttachments(true)
      setSelectedFileAttachments(attachments => {
        const map = new Map(attachments)
        map.has(f.id) ? map.delete(f.id) : map.set(f.id, f)
        return map
      })
    },
    [isSelectingFileAttachments]
  )
  const {download, isDownloading} = useDownloadFileAttachments({
    onDownload: onToggleIsSelectingFileAttachments,
  })
  const downloadFileAttachments = useCallback(
    () => download([...selectedFileAttachments.values()]),
    [download, selectedFileAttachments]
  )
  const {
    data: {chat_message_file_attachments: chatMessageFileAttachments = []} = {},
    fetchMore,
  } = useQuery<QueryRoot['chat_message_file_attachments']>(
    CHAT_MESSAGE_FILE_ATTACHMENTS_QUERY, {
      fetchPolicy: 'cache-and-network',
      variables: {
        chatId,
        mimeTypePattern: {
          DOCUMENTS: 'application/pdf',
          MEDIA: '(video|image)/%',
        }[activeTab],
      },
    },
  )
  useEffect(() => {
    offsetRef.current = LOAD_MORE_OFFSET
    hasEarlierChatMessageFileAttachmentsRef.current = true
  }, [activeTab])
  const fetchEarlierFileAttachments = useCallback(() => {
    if (!hasEarlierChatMessageFileAttachmentsRef.current) return
    fetchMore({
      updateQuery: (
        {
          chat_message_file_attachments: previousChatMessageFileAttachments = [],
        } = {} as any,
        {
          fetchMoreResult: {
            chat_message_file_attachments: moreChatMessageFileAttachments,
          } = {},
        },
      ) => {
        if (moreChatMessageFileAttachments?.length) {
          offsetRef.current = offsetRef.current + LOAD_MORE_OFFSET
        }
        hasEarlierChatMessageFileAttachmentsRef.current =
          !!moreChatMessageFileAttachments?.length
        return {
          chat_message_file_attachments: unionBy(
            previousChatMessageFileAttachments, moreChatMessageFileAttachments, 'id'
          ),
        } as QueryResultWrapper<QueryRoot['chat_message_file_attachments']>
      },
      variables: {offset: offsetRef.current},
    })
  }, [fetchMore])
  const groupNameToChatMessageFileAttachments = useMemo<Record<any, any>>(
    () => groupBy(
      chatMessageFileAttachments,
      a => {
        const date = new Date(a.insertionTimestamp)
        if (isThisWeek(date)) return translate('periods.recent')
        if (isThisMonth(date)) return translate('periods.month')
        if (differenceInYears(new Date(), date) > 1) {
          // The prefixed empty space is important so that lodash's groupBy preserves
          // the order. groupBy changes the order for numbers or string that can be
          // interpreted as a number.
          return ` ${date.getFullYear()}`
        }
        return monthNameFromData(a.insertionTimestamp, locale)
      },
    ), [chatMessageFileAttachments, locale, translate],
  )
  return (
    <Box className={styles.container}>
      <div className={styles.header}>
        <IconButton onClick={onClose}><CloseIcon color="secondary" /></IconButton>
        <span>{translate('chat.chatMessageFileAttachmentListSidebar.title')}</span>
      </div>
      <div className={styles.mainContent}>
        <Tabs
          TabIndicatorProps={{children: <span/>}}
          value={activeTab}
          variant="standard"
        >
          {TAB_FILTERS.map((filter, i) => (
            <Tab
              className={styles.tabRoot}
              component="a"
              disableRipple
              key={filter}
              wrapped
              label={
                <div>
                  {translate(
                    'chat.chatMessageFileAttachmentListSidebar.filters.' +
                    `${filter.toLowerCase()}`,
                  )}
                </div>
              }
              onClick={() => setActiveTab(TAB_FILTERS[i])}
              value={filter}
            />
          ))}
        </Tabs>
        <br/>
        <div className={styles.chatMessageFileAttachmentList}>
          {activeTab === 'MEDIA' && (
            <MediaTab
              chatMessageFileAttachments={chatMessageFileAttachments}
              fileAttachmentActionButtonProps={{
                isSelectingFileAttachments,
                onToggleSelectedFileAttachments,
                selectedFileAttachments,
              }}
              groupNameToChatMessageFileAttachments={
                groupNameToChatMessageFileAttachments
              }
              onLoadMore={fetchEarlierFileAttachments}
              onOpenAttachmentInDialog={a => {
                setIsDialogOpen(true)
                setSelectedFileAttachment(a)
              }}
            />
          )}
          {activeTab === 'DOCUMENTS' && (
            <DocumentsTab
              chatMessageFileAttachments={chatMessageFileAttachments}
              fileAttachmentActionButtonProps={{
                isSelectingFileAttachments,
                onToggleSelectedFileAttachments,
                selectedFileAttachments,
              }}
              groupNameToChatMessageFileAttachments={
                groupNameToChatMessageFileAttachments
              }
              onLoadMore={fetchEarlierFileAttachments}
              onOpenAttachmentInDialog={a => {
                setIsDialogOpen(true)
                setSelectedFileAttachment(a)
              }}
            />
          )}
          {selectedFileAttachment && (
            <FileAttachmentsSwiperDialog
              chatMessageFileAttachments={chatMessageFileAttachments.toReversed()}
              value={selectedFileAttachment}
              isOpen={isDialogOpen}
              onOpen={setIsDialogOpen}
            />
          )}
        </div>
      </div>
      {isSelectingFileAttachments && (
        <div className={styles.downloadFileAttachmentsToolbar}>
          <IconButton onClick={onToggleIsSelectingFileAttachments}>
            <CloseIcon color="inherit"/>
          </IconButton>
          <Typography variant="subtitle2">
            {translate('ra.action.bulk_actions', {
              smart_count: selectedFileAttachments.size,
            })}
          </Typography>
          <IconButton
            disabled={!selectedFileAttachments.size || isDownloading}
            onClick={downloadFileAttachments}
          >
            {isDownloading ?
              <CircularProgress color="secondary" size="1.5rem" /> :
              <DownloadIcon />
            }
          </IconButton>
        </div>
      )}
    </Box>
  )
}

const DocumentsTab: FC<DocumentsTabProps> = ({
  chatMessageFileAttachments,
  fileAttachmentActionButtonProps,
  groupNameToChatMessageFileAttachments,
  onLoadMore,
  onOpenAttachmentInDialog,
}) => {
  const {classes: styles} = useStyles({})
  const groupNames = useMemo<string[]>(
    () => Object.keys(groupNameToChatMessageFileAttachments),
    [groupNameToChatMessageFileAttachments],
  )
  const groupCounts = useMemo<number[]>(
    () => Object.values(groupNameToChatMessageFileAttachments).map(
      messages => messages.length,
    ), [groupNameToChatMessageFileAttachments],
  )
  const {getFilesApiUrl} = useContext(ApiUrlsContext)
  const getThumbnailUrl = useCallback((a: ChatMessageFileAttachments) => {
    const isImageFile = (/image\/\w+/).test(a.mimeType)
    if (isImageFile) return getFilesApiUrl(a.key)
    if (a.thumbnailMediaKey) return getFilesApiUrl(a.thumbnailMediaKey)
    return undefined
  }, [getFilesApiUrl])
  const canFetchMore = useCanFetchMore(chatMessageFileAttachments)
  return (
    <GroupedVirtuoso
      atBottomThreshold={20}
      atBottomStateChange={atBottom => atBottom && canFetchMore && onLoadMore()}
      computeItemKey={index => chatMessageFileAttachments[index]?.id ?? index}
      groupContent={index => (
        <Group groupName={groupNames[index]} />
      )}
      groupCounts={groupCounts}
      itemContent={index => {
        const a = chatMessageFileAttachments[index]
        const thumbnailUrl = getThumbnailUrl(a)
        return (
          <FileAttachmentContainer
            className={styles.documentListRow}
            fileAttachment={a}
            key={a.key}
            onOpenAttachmentInDialog={onOpenAttachmentInDialog}
            {...fileAttachmentActionButtonProps}
          >
            <FileAttachmentActionButton
              fileAttachment={a}
              {...fileAttachmentActionButtonProps }
            />
            {thumbnailUrl && (
              <img alt="" className={styles.documentImage} src={thumbnailUrl}/>
            )}
            <Typography variant="subtitle2">
              {a.filename.length > 30 ?
                `${a.filename.slice(0, 15)} ... ${a.filename.slice(-15)}` :
                a.filename
              }
            </Typography>
          </FileAttachmentContainer>
        )
      }}
    />
  )
}

const MediaTab: FC<MediaTabProps> = ({
  chatMessageFileAttachments,
  fileAttachmentActionButtonProps,
  groupNameToChatMessageFileAttachments,
  onLoadMore,
  onOpenAttachmentInDialog,
}) => {
  const {isSelectingFileAttachments} = fileAttachmentActionButtonProps
  const {classes: styles} = useStyles({isSelectingFileAttachments})
  const groupNames = useMemo<string[]>(
    () => Object.keys(groupNameToChatMessageFileAttachments),
    [groupNameToChatMessageFileAttachments],
  )
  const groupNameToChunkedChatMessageFileAttachments = useMemo<
    Record<string, ChatMessageFileAttachments[][]>
  >(
    () => Object.keys(groupNameToChatMessageFileAttachments).reduce((prev, groupName) => {
      const attachments = groupNameToChatMessageFileAttachments[groupName]
      prev[groupName] = chunk(attachments, MEDIA_ITEMS_PER_ROW)
      return prev
    }, {}),
    [groupNameToChatMessageFileAttachments],
  )
  // Specifies the amount of items in each group
  // (and, actually, how many groups are there). For example, passing [20, 30]
  // will display 2 groups with 20 and 30 items each.
  const groupCounts = useMemo<number[]>(
    () => Object.values(groupNameToChunkedChatMessageFileAttachments).map(
      messages => messages.length,
    ), [groupNameToChunkedChatMessageFileAttachments],
  )
  const {getFilesApiUrl} = useContext(ApiUrlsContext)
  const canFetchMore = useCanFetchMore(chatMessageFileAttachments)
  if (!chatMessageFileAttachments.length) return null
  return (
    <Box height="100%">
      <GroupedVirtuoso
        atBottomThreshold={20}
        atBottomStateChange={atBottom => atBottom && canFetchMore && onLoadMore()}
        groupContent={index => (
          <Group groupName={groupNames[index]} />
        )}
        groupCounts={groupCounts}
        itemContent={index => {
          const chunkedFilesAttachments: ChatMessageFileAttachments[][] = Object.values(
            groupNameToChunkedChatMessageFileAttachments,
          ).flat()
          const attachments: ChatMessageFileAttachments[] = chunkedFilesAttachments[index]
          return (
            <Box className={styles.mediaListRow}>
              {attachments.map(a => (
                <FileAttachmentContainer
                  className={styles.fileAttachmentContainer}
                  fileAttachment={a}
                  key={a.key}
                  onOpenAttachmentInDialog={onOpenAttachmentInDialog}
                  {...fileAttachmentActionButtonProps}
                >
                  <FileAttachmentActionButton
                    fileAttachment={a}
                    {...fileAttachmentActionButtonProps }
                  />
                  <img alt={a.filename} className={styles.media}
                    src={getFilesApiUrl(a.thumbnailMediaKey ?? a.key)}
                  />
                </FileAttachmentContainer>
              ))}
            </Box>
          )
        }}
      />
    </Box>
  )
}

const FileAttachmentContainer = ({
  children,
  className,
  fileAttachment,
  isSelectingFileAttachments,
  onOpenAttachmentInDialog,
  onToggleSelectedFileAttachments,
}: FileAttachmentContainerProps) => {
  const longPressEventHandlers = useLongPressEvent({
    onEnter: () => onToggleSelectedFileAttachments(fileAttachment),
  })
  const onClick = useCallback(
    () => isSelectingFileAttachments ?
      onToggleSelectedFileAttachments(fileAttachment) :
      onOpenAttachmentInDialog(fileAttachment),
    [
      fileAttachment,
      isSelectingFileAttachments,
      onOpenAttachmentInDialog,
      onToggleSelectedFileAttachments,
    ]
  )
  return (
    <div
      className={className}
      onClick={onClick}
      {...(!isSelectingFileAttachments && longPressEventHandlers)}
    >
      {children}
    </div>
  )
}

// The Virtuoso list component fetches attachments each time the scroll reaches the bottom
// of the page. This creates a peculiar behavior where another GraphQL query is executed
// immediately after the previous one returns its result.
// To prevent that, we use "useCanFetchMore" to control when the next fetch
// can be performed.
const useCanFetchMore = (chatMessageFileAttachments: ChatMessageFileAttachments[]) => {
  const [canFetchMore, setCanFetchMore] = useState(false)
  useEffect(() => {
    if (!chatMessageFileAttachments.length) return
    const timeoutId = setTimeout(() => setCanFetchMore(true), 100)
    return () => {
      clearTimeout(timeoutId)
      setCanFetchMore(false)
    }
  }, [chatMessageFileAttachments, setCanFetchMore])
  return canFetchMore
}

const Group: FC<GroupProps> = ({groupName}) => {
  const {classes: styles} = useStyles({})
  return (
    <Grid2 className={styles.groupGrid} key={groupName}>
      <Typography variant="subtitle2">{groupName}</Typography>
    </Grid2>
  )
}

const FileAttachmentActionButton = ({
  fileAttachment,
  isSelectingFileAttachments,
  onToggleSelectedFileAttachments,
  selectedFileAttachments,
}: FileAttachmentPopperProps) => {
  const translate = useTranslate()
  const {classes: styles} = useStyles({})
  const [isOpen, setIsOpen] = useState(false)
  const anchorElementRef = useRef<HTMLButtonElement | null>(null)
  return (
    <div className={styles.fileAttachmentActionButton}>
      {isSelectingFileAttachments ? (
        <Checkbox
          onClick={e => {
            e.stopPropagation()
            onToggleSelectedFileAttachments(fileAttachment)
          }}
          checked={selectedFileAttachments.has(fileAttachment.id)}
        />
      ) : (
        <IconButton
          aria-label="file-attachment-actions"
          onClick={e => {
            e.stopPropagation()
            setIsOpen(isOpen_ => !isOpen_)
          }}
          ref={anchorElementRef}
          size="small"
        >
          <MoreVertIcon size="inherit" />
        </IconButton>
      )}
      <Popper
        anchorEl={anchorElementRef.current}
        modifiers={[
          {enabled: true, name: 'arrow', options: {element: anchorElementRef.current}},
        ]}
        open={isOpen}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
          <Paper
            className={styles.popperPaper}
            elevation={3}
            onClick={e => e.stopPropagation()}
          >
            <MenuItem
              onClick={() => {
                setIsOpen(false)
                onToggleSelectedFileAttachments(fileAttachment)
              }}
            >
              <ListItemIcon>
                <DownloadIcon size="small" color="secondary" />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" color="primary">
                  {translate('actions.download')}
                </Typography>
              </ListItemText>
            </MenuItem>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  )
}

const LOAD_MORE_OFFSET = 6
const MEDIA_ITEMS_PER_ROW = 3
const TAB_FILTERS: TabFilter[] = ['MEDIA', 'DOCUMENTS']

// Using a 16:9 screen ratio plus a 20%-ish height for each image (calculated backwards
// from 38% width of sidebar, multiplied by 30% width - hence height - of each image), we
// need at least 5 rows (6 rows to be safe) of images loaded, so that we can scroll down
// to load more images. Hence, the `limit` used becomes `3 * 6 = 18`.
const CHAT_MESSAGE_FILE_ATTACHMENTS_QUERY = gql`query(
  $chatId: uuid!, $mimeTypePattern: String!, $offset: Int! = 0
){
  chat_message_file_attachments(
    limit: 18
    offset: $offset
    order_by: {insertionTimestamp: desc}
    where: {
      chatMessage: {chatId: {_eq: $chatId}}
      mimeType: {_similar: $mimeTypePattern}
    }
  ){filename id insertionTimestamp key mimeType thumbnailMediaKey}
}`

const useStyles = makeStyles<MakeStylesProps, 'fileAttachmentActionButton'>()((
  theme, {isSelectingFileAttachments}, classes
) => {
  const fileAttachmentsRow = {
    WebkitTapHighlightColor: 'transparent',
    cursor: 'pointer',
    userSelect: 'none' as CSSObject['userSelect'],
  }
  return (
    {
      chatMessageFileAttachmentList: {
        height: '100%',
        overflowY: 'auto',
      },
      container: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      },
      documentImage: {
        borderRadius: '8px',
        height: theme.remSpacing(10),
        objectFit: 'cover',
        width: theme.remSpacing(8),
      },
      documentListRow: {
        ...fileAttachmentsRow,
        ...(!isSelectingFileAttachments && {
          [theme.breakpoints.up('lg')]: {
            [`&:not(:hover) > .${classes.fileAttachmentActionButton}`]: {
              display: 'none',
            },
          },
        }),
        alignItems: 'center',
        display: 'flex',
        gap: '10px',
        marginTop: theme.remSpacing(2),
        paddingBottom: '10px',
        position: 'relative',
        wordBreak: 'break-word',
      },
      downloadFileAttachmentsToolbar: {
        [theme.breakpoints.down('lg')]: {
          marginBottom: `calc(var(--safe-area-bottom-inset, 1rem) - 1rem)`,
        },
        '& > :last-child': {
          marginLeft: 'auto',
        },
        alignItems: 'center',
        background: theme.palette.background.paper,
        color: theme.palette.secondary.main,
        display: 'flex',
        gap: theme.remSpacing(2),
        padding: `${theme.remSpacing(1.65)} ${theme.remSpacing(2)}`,
      },
      fileAttachmentActionButton: {
        color: theme.palette.info.main,
        fontSize: theme.typography.button.fontSize,
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 1,
      },
      fileAttachmentContainer: {
        ...(!isSelectingFileAttachments && {
          [theme.breakpoints.up('lg')]: {
            [`&:not(:hover) > .${classes.fileAttachmentActionButton}`]: {
              display: 'none',
            },
          },
        }),
        position: 'relative',
        width: '30%',
      },
      groupGrid: {
        marginBottom: theme.remSpacing(2),
        marginTop: theme.remSpacing(2),
      },
      header: {
        alignItems: "center",
        borderBottom: `solid 1px ${theme.palette.disabled.main}`,
        display: 'flex',
        gap: '6px',
        height: theme.remSpacing(8),
        minHeight: theme.remSpacing(8),
      },
      mainContent: {
        flexGrow: 1,
        overflowY: 'hidden',
        padding: theme.remSpacing(2),
      },
      media: {
        aspectRatio: '1/1',
        objectFit: 'cover',
        width: '100%',
      },
      mediaListRow: {
        ...fileAttachmentsRow,
        display: 'flex',
        gap: theme.remSpacing(1),
        marginTop: theme.remSpacing(2),
      },
      popperPaper: {
        borderRadius: theme.remSpacing(1.5),
        boxShadow: '-4px 4px 14px 0px rgba(0, 0, 0, 0.15)',
        padding: `${theme.remSpacing(0.5)} 0`,
      },
      tabRoot: {
        [theme.breakpoints.down('sm')]: {
          paddingLeft: theme.remSpacing(1.5),
          paddingRight: theme.remSpacing(1.5),
        },
        [theme.breakpoints.down('xs')]: {
          paddingLeft: theme.remSpacing(.5),
          paddingRight: theme.remSpacing(.5),
        },
        '& .MuiTab-wrapper': {
          flexDirection: 'row',
        },
      },
    }
  )
})

interface ChatMessageFileAttachmentListSidebarProps {
  chatId: string
  onClose: VoidFunction
}

type DocumentsTabProps = TabProps

type MediaTabProps = TabProps

interface TabProps {
  chatMessageFileAttachments: ChatMessageFileAttachments[]
  fileAttachmentActionButtonProps: Omit<FileAttachmentPopperProps, 'fileAttachment'>
  groupNameToChatMessageFileAttachments: {
    [index: string]: ChatMessageFileAttachments[]
  }
  onLoadMore: VoidFunction
  onOpenAttachmentInDialog: OnOpenAttachmentInDialog
}

interface FileAttachmentContainerProps {
  children: ReactNode | Iterable<ReactNode>
  className?: string
  fileAttachment: ChatMessageFileAttachments
  isSelectingFileAttachments: boolean
  onOpenAttachmentInDialog: OnOpenAttachmentInDialog
  onToggleSelectedFileAttachments: OnToggleSelectedFileAttachments
}

interface FileAttachmentPopperProps {
  fileAttachment: ChatMessageFileAttachments
  isSelectingFileAttachments: boolean
  onToggleSelectedFileAttachments: OnToggleSelectedFileAttachments
  selectedFileAttachments: SelectedFileAttachments
}

interface GroupProps {
  groupName: string
}

interface MakeStylesProps {
  isSelectingFileAttachments?: boolean
}

type SelectedFileAttachments = Map<
  ChatMessageFileAttachments['id'],
  ChatMessageFileAttachments
>

type OnOpenAttachmentInDialog = (a: ChatMessageFileAttachments) => void

type OnToggleSelectedFileAttachments = (_: ChatMessageFileAttachments) => void

type TabFilter = 'DOCUMENTS' | 'MEDIA'
export default ChatMessageFileAttachmentListSidebar
